<template>
  <section>
    <div class="grid-x grid-margin-x">
      <div class="cell large-4">
        <section-header size="sm" class="mb-3">
          <heading :level="4" size="sm" qaid="warranty-claim-line-issue-details" v-html="bp('issueDetails')" />
        </section-header>
        <form-group
          v-model="claimLine.issueTypeNameSelected"
          v-validate="'required'"
          type="text"
          qaid="warranty-claim-line-issue-type-label"
          required
          :name="inputName('issueTypeNameSelected')"
          :error="errors.first(inputName('issueTypeNameSelected'))"
          :show-error-messages="true"
          :data-vv-as="bp('issueType')"
          :label="bp('issueType')"
          :auto-complete-options="warrantyIssuesAutocomplete"
          :disabled="!issueDetailsEnabled"
          @blur="handleIssueTypeInput"
        />

        <div class="form-group">
          <span
            v-show="!issueDetailsEnabled"
            class="text-secondary cursor-not-allowed"
            v-html="getBaseProperty('issueDescriptionChart')"
          />
          <a
            v-show="issueDetailsEnabled"
            ref="tippy"
            v-tippy="{
              html: '#issue-types-panel',
              interactive: true,
              reactive: true,
              trigger: 'click',
              placement: 'right',
            }"
            class="underline hover:no-underline"
            qaid="warranty-claim-line-issue-chart"
            @hidden="resetTippy"
            v-html="bp('issueDescriptionChart')"
          />
        </div>

        <warranty-claim-line-issue-types
          id="issue-types-panel"
          class="text-md p-1"
          :warranty-issues="warrantyIssues"
          :reset-issues="resetTippyIssues"
          :base-properties="baseProperties"
          @issue-selected="issueSelected"
        />

        <form-group
          type="radio"
          :label="bp('issueWhen')"
          qaid="warranty-claim-line-issue-when"
          :multi-items="damageOccurredMethodOptions"
          :value="claimLine.selectedDamagedOccurred"
          :name="inputName('issue-damage-occurred-method')"
          @change="handleDamageOccurredMethodhange"
        />
        <form-group
          type="radio"
          :label="bp('issueRequestedAction')"
          qaid="warranty-claim-line-issue-action"
          :multi-items="requestedActionOptions"
          :value="claimLine.selectedRequestedAction"
          :name="inputName('issue-requested-action')"
          @change="handleRequestedActionChange"
        />

        <fieldset v-if="displayWarrantyShipmentOptions">
          <div id="warranty-claim-line-issue-shipment-options-tooltip" v-tippy-html class=" hidden ">
            <span class="inline-block text-left" v-html="bp('customerWaiting')"></span>
          </div>

          <label class="form-label">
            <span v-html="bp('shipmentOptions')"></span>
            <i
              v-tippy="{
                html: `#warranty-claim-line-issue-shipment-options-tooltip `,
                placement: 'top-start',
                arrow: true,
                maxWidth: '20rem',
                interactive: true,
              }"
              class="material-icons md-18 ml-25 self-center"
              >help</i
            >
          </label>

          <ul>
            <li v-for="(shipmentOption, index) in getShipmemtOptions" :key="index">
              <label class="radio">
                <input
                  v-model="claimLine.warrantyConsumerWaiting"
                  type="radio"
                  :name="inputName('shipmentOption' + '-' + shipmentOption.value)"
                  :qaid="inputName('shipmentOption' + '-' + shipmentOption.value)"
                  :value="shipmentOption.value"
                  @change="handleShipmentOptionChange"
                />
                <span class="control-indicator"></span>
                <span v-html="shipmentOption.displayName"> </span>
              </label>
            </li>
          </ul>
        </fieldset>
      </div>
    </div>
  </section>
</template>

<script>
import SectionHeader from '@/components/SectionHeader';
import Heading from '@/components/Heading';
import FormGroup from '@/components/FormGroup';
import find from 'lodash/find';
import {mapActions, mapState} from 'vuex';
import WarrantyClaimLineIssueTypes from '@/components/containers/warranty-claim/WarrantyClaimLineIssueTypes';

export default {
  components: {FormGroup, SectionHeader, Heading, WarrantyClaimLineIssueTypes},
  inject: ['$validator'],

  props: {
    baseProperties: {
      type: Array,
      required: true,
    },
    claimData: {
      type: Object,
      required: true,
    },
    claimLine: {
      type: Object,
      required: true,
    },
    claimIndex: {
      type: Number,
      required: false,
      default: 0,
    },
    issueDetailsEnabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    warrantyIssues: {
      type: Array,
      required: true,
    },
    filterIssuesThreshold: {
      type: Number,
      default: 100,
    },
    displayWarrantyShipmentOptions: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      resetTippyIssues: false,
      flatWarrantyIssues: null,
    };
  },

  computed: {
    // Pre-filters the warranty issues so that jquery autocomplete doesn't freeze the browser when there are too many options
    warrantyIssuesAutocomplete() {
      if (this.warrantyIssues) {
        let tempIssues = this.getFlatWarrantyIssues(this.warrantyIssues, null, true);

        if (
          tempIssues &&
          tempIssues.length > this.filterIssuesThreshold &&
          Array.isArray(tempIssues) &&
          this.claimLine.issueTypeNameSelected
        ) {
          tempIssues = tempIssues.filter((issue) => {
            if (typeof issue === 'string') {
              return issue.toLowerCase().startsWith(this.claimLine.issueTypeNameSelected.toLowerCase());
            }
          });
        }

        return tempIssues;
      }

      return [];
    },
    getShipmemtOptions() {
      return [
        {value: false, displayName: this.bp('nextScheduled')},
        {value: true, displayName: this.bp('shipNow')},
      ];
    },
    requestedActionOptions() {
      if (!this.claimData.requestedActionMethods) {
        return [];
      }

      return this.claimData.requestedActionMethods.map((requestedAction) => ({
        value: requestedAction.code,
        displayName: requestedAction.name,
      }));
    },

    damageOccurredMethodOptions() {
      if (!this.claimData.damageOccurredMethods) {
        return [];
      }

      return this.claimData.damageOccurredMethods.map((damageMethod) => ({
        value: damageMethod.code,
        displayName: damageMethod.name,
      }));
    },

    ...mapState('warrantyClaims', ['validators']),
  },

  watch: {
    warrantyIssues() {
      this.flatWarrantyIssues = this.getFlatWarrantyIssues(this.warrantyIssues);
      this.resetTippy();
    },
  },

  mounted() {
    this.flatWarrantyIssues = this.getFlatWarrantyIssues(this.warrantyIssues);
    this.validators.push(this.$validator);
    this.claimLine.warrantyConsumerWaiting = this.claimLine.warrantyConsumerWaiting
      ? this.claimLine.warrantyConsumerWaiting
      : false;
  },

  methods: {
    inputName(propertyName) {
      return `cl${this.claimIndex}_${propertyName}`;
    },

    getFlatWarrantyIssues(warrantyIssues, options, ufoNameOnly) {
      if (!options) options = [];

      if (warrantyIssues && warrantyIssues.length) {
        warrantyIssues.forEach((issue) => {
          if (issue.warrantyIssueTypes && issue.warrantyIssueTypes.length) {
            // Issue has sub-issues. Recusively add them
            options = this.getFlatWarrantyIssues(issue.warrantyIssueTypes, options, ufoNameOnly);
          } else {
            // Issue doesn't have sub-issues. Add it to the result

            if (issue.warrantyUfoName && issue.warrantyUfoCode) {
              if (ufoNameOnly) {
                options.push(issue.warrantyUfoName);
              } else {
                options.push(issue);
              }
            }
          }
        });
      }

      return options;
    },

    issueSelected(input) {
      this.claimLine.issueTypeNameSelected = input;

      let issueObject = find(this.flatWarrantyIssues, (issue) => issue.warrantyUfoName === input);

      if (issueObject && issueObject.warrantyUfoCode && issueObject.salesCategorySRP5) {
        this.claimLine.issueTypeCodeSelected = issueObject.warrantyUfoCode;
        this.claimLine.salesCategorySRP5Code = issueObject.salesCategorySRP5;

        this.claimLine.warrantyRequiredImagesList = issueObject.warrantyRequiredImages;
      } else {
        this.claimLine.issueTypeCodeSelected = null;
        this.claimLine.salesCategorySRP5Code = null;

        this.claimLine.warrantyRequiredImagesList = [];
      }

      this.$forceUpdate();
      this.closeTooltip();

      this.$nextTick(() => this.$validator.validate(this.inputName('issueTypeNameSelected')));

      this.autoSaveClaim({csrf: this.csrf});
    },

    resetTippy() {
      this.resetTippyIssues = true;

      // Reset boolean on a timer so we can use it again later
      setTimeout(() => {
        this.resetTippyIssues = false;
      }, 1000);
    },

    closeTooltip() {
      let el = this.$refs.tippy;

      if (el && el._tippy) {
        el._tippy.hide();
      }
    },

    handleIssueTypeInput(e) {
      this.issueSelected(e.target.value);
    },

    handleRequestedActionChange(e) {
      this.claimLine.selectedRequestedAction = e.target.value;
      this.autoSaveClaim({csrf: this.csrf});
    },

    handleShipmentOptionChange(e) {
      this.claimLine.warrantyConsumerWaiting = e.target.value;
      this.autoSaveClaim({csrf: this.csrf});
    },
    handleDamageOccurredMethodhange(e) {
      this.claimLine.selectedDamagedOccurred = e.target.value;
      this.autoSaveClaim({csrf: this.csrf});
    },

    ...mapActions('warrantyClaims', ['autoSaveClaim']),
  },
};
</script>
